/* eslint-disable */
/**
 * AgenterPanel.jsx — Panel i Sager-stil til OS-dashboard
 *
 * Datapunkter og status-mærkning:
 *   /api/os/hjerne/overblik   → LIVE
 *     Felter: agenter[{agent_id, display_navn, status, aktuel_opgave,
 *             sidst_aktiv, opdateret_kl, er_aktiv, inaktiv_timer}],
 *             ulæste_beskeder, laering_uge, afventende_beslutninger,
 *             agent_aktiv_timer_graense, aktive_projekter
 *   /api/os/hjerne/kommunikation?limit=12  → LIVE (tidslinje)
 *     Felter: kommunikation[{id, fra_agent, til_agent, emne, oprettet_kl}]
 *
 * Regler:
 *   - Præcis de 18 kanoniske agenter (arkiveret=false fra DB) — aldrig 70/74/75
 *   - TomTilstand hvis endpoint fejler
 *   - Statusfarver: grøn=aktiv, amber=afventer, grå=inaktiv
 *   - agent_aktiv_timer_graense bruges til at bestemme farve (fra DB, ikke hardkodet)
 */

import React, { useState, useCallback } from 'react';
import {
  KpiKort,
  StatusBadge,
  TomTilstand,
  TidslinjeListe,
  SektionsHeader,
  AfventerBadge,
  ModalSkal,
  useApiPolling,
  STATUS,
  TEKST,
  BG,
  KANT,
  RADIUS,
  FONT,
} from '../komponenter/index';
import { Boks } from '../ui/Boks';

// ─── Hjælpefunktioner ─────────────────────────────────────────────────────────

function formatRelativTid(iso) {
  if (!iso) return 'Aldrig';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1)   return 'Lige nu';
  if (min < 60)  return `${min} min`;
  const t = Math.floor(min / 60);
  if (t < 24)    return `${t}t`;
  const d = Math.floor(t / 24);
  return `${d} dag${d !== 1 ? 'e' : ''}`;
}

// ─── Status-hjælpefunktioner (agent-sprog — ikke integration-sprog) ──────────
// Mapping:
//   er_aktiv=true                            → AKTIV  (grøn)  — kører lige nu
//   er_aktiv=false + sidst_aktiv < grænse   → AKTIV  (grøn)  — kørte for nylig (normalt for on-demand)
//   er_aktiv=false + sidst_aktiv > grænse   → STANDBY (grå)  — ikke kørt for nylig (normalt, ikke en fejl)
//   er_aktiv=false + sidst_aktiv = null     → STANDBY (grå)  — aldrig set (normalt for ubrugte agenter)
//   status = 'FEJLET' i DB                  → FEJL_AGENT (rød)
function agentStatusFarve(agent, timerGraense) {
  if (!agent) return STATUS.ikkeForbundet;
  if (agent.status === 'FEJLET') return STATUS.fejl;
  if (agent.er_aktiv) return STATUS.live;
  if (!agent.sidst_aktiv) return STATUS.ikkeForbundet;
  const timer = (Date.now() - new Date(agent.sidst_aktiv).getTime()) / 3600000;
  if (timer < (timerGraense || 24)) return STATUS.live;
  return STATUS.ikkeForbundet;
}

function agentStatusTekst(agent, timerGraense) {
  if (!agent) return 'Ukendt';
  if (agent.status === 'FEJLET') return 'Fejl';
  if (agent.er_aktiv) return 'Aktiv';
  if (!agent.sidst_aktiv) return 'Standby';
  const timer = (Date.now() - new Date(agent.sidst_aktiv).getTime()) / 3600000;
  if (timer < (timerGraense || 24)) return 'Aktiv';
  return 'Standby';
}

function agentStatusBadge(agent, timerGraense) {
  if (!agent) return 'STANDBY';
  if (agent.status === 'FEJLET') return 'FEJL_AGENT';
  if (agent.er_aktiv) return 'AKTIV';
  if (!agent.sidst_aktiv) return 'STANDBY';
  const timer = (Date.now() - new Date(agent.sidst_aktiv).getTime()) / 3600000;
  if (timer < (timerGraense || 24)) return 'AKTIV';
  return 'STANDBY';
}


// ─── Agent-detalje modal ──────────────────────────────────────────────────────

function AgentDetaljeModal({ agent, timerGraense, onLuk }) {
  if (!agent) return null;
  const badgeStr = agentStatusBadge(agent, timerGraense);
  const farve    = agentStatusFarve(agent, timerGraense);

  const felt = (label, value, mono = false) => value == null ? null : (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 14 }}>
      <span style={{ fontSize: 10, color: TEKST.dæmpet, textTransform: 'uppercase', letterSpacing: 0.7 }}>{label}</span>
      <span style={{
        fontSize: 13, color: TEKST.primær, fontWeight: 500,
        fontFamily: mono ? FONT.mono : FONT.family,
        wordBreak: 'break-all',
      }}>{value}</span>
    </div>
  );

  const kpiData = agent.kpi_data && typeof agent.kpi_data === 'object'
    ? Object.entries(agent.kpi_data) : [];

  return (
    <ModalSkal åben={true} onLuk={onLuk} titel={agent.display_navn || agent.agent_id} bredde={500}>
      {/* Status-oversigt */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20,
        padding: '12px 14px', background: 'rgba(255,255,255,0.03)',
        borderRadius: RADIUS.kort - 2, border: `1px solid ${KANT.fin}` }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: farve, flexShrink: 0,
          boxShadow: agent.er_aktiv ? `0 0 7px ${farve}` : 'none' }} />
        <StatusBadge status={badgeStr} tekst={agentStatusTekst(agent, timerGraense)} />
        <span style={{ fontSize: 12, color: TEKST.sekundær, marginLeft: 'auto' }}>
          Sidst aktiv: <strong style={{ color: TEKST.primær }}>{formatRelativTid(agent.sidst_aktiv)}</strong>
        </span>
      </div>

      {/* Hvad agenten sidst lavede */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 11, color: TEKST.dæmpet, textTransform: 'uppercase',
          letterSpacing: 0.7, marginBottom: 8 }}>Seneste aktivitet</div>
        <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: RADIUS.knap,
          border: `1px solid ${KANT.fin}`, padding: '12px 14px' }}>
          {agent.aktuel_opgave ? (
            <span style={{ fontSize: 13, color: TEKST.primær }}>{agent.aktuel_opgave}</span>
          ) : agent.status_tekst ? (
            <span style={{ fontSize: 13, color: TEKST.sekundær, fontStyle: 'italic' }}>{agent.status_tekst}</span>
          ) : (
            <span style={{ fontSize: 13, color: TEKST.dæmpet, fontStyle: 'italic' }}>Ingen registreret opgave</span>
          )}
        </div>
      </div>

      {/* Detalje-felter */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px' }}>
        {felt('Agent-id', agent.agent_id, true)}
        {felt('Sidst aktiv', agent.sidst_aktiv
          ? new Date(agent.sidst_aktiv).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' })
          : 'Aldrig')}
        {felt('Afdeling', agent.afd_navn || null)}
        {felt('Division', agent.division_navn || null)}
        {agent.afventende_opgaver > 0 && felt('Afventende opgaver', agent.afventende_opgaver)}
        {agent.inaktiv_timer != null && agent.inaktiv_timer > 0 &&
          felt('Inaktiv i', `${Math.round(agent.inaktiv_timer)}t`)}
        {felt('Rapport opdateret', agent.rapport_opdateret
          ? new Date(agent.rapport_opdateret).toLocaleString('da-DK', { dateStyle: 'short', timeStyle: 'short' })
          : null)}
      </div>

      {/* KPI-data */}
      {kpiData.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ fontSize: 11, color: TEKST.dæmpet, textTransform: 'uppercase',
            letterSpacing: 0.7, marginBottom: 10 }}>KPI-data</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8 }}>
            {kpiData.map(([k, v]) => (
              <div key={k} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: RADIUS.knap,
                border: `1px solid ${KANT.fin}`, padding: '8px 12px' }}>
                <div style={{ fontSize: 10, color: TEKST.dæmpet, marginBottom: 3 }}>{k}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: TEKST.primær }}>
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Forbedringsforslag */}
      {agent.forbedringsforslag && (
        <div style={{ marginTop: 16, padding: '10px 14px',
          background: 'rgba(245,158,11,0.06)', borderRadius: RADIUS.knap,
          border: '1px solid rgba(245,158,11,0.20)' }}>
          <div style={{ fontSize: 10, color: '#fbbf24', textTransform: 'uppercase',
            letterSpacing: 0.7, marginBottom: 6 }}>Forbedringsforslag</div>
          <div style={{ fontSize: 12, color: TEKST.sekundær }}>{agent.forbedringsforslag}</div>
        </div>
      )}

      {/* Statusforklaring */}
      <div style={{ marginTop: 20, padding: '10px 14px',
        background: 'rgba(255,255,255,0.02)', borderRadius: RADIUS.knap,
        border: `1px solid ${KANT.fin}`, fontSize: 11, color: TEKST.dæmpet, lineHeight: 1.6 }}>
        <strong style={{ color: TEKST.sekundær }}>Om agent-status:</strong>{' '}
        <span style={{ color: STATUS.live, fontWeight: 600 }}>Aktiv</span> = kørte inden for {timerGraense}t
        (normalt for on-demand-agenter).{' '}
        <span style={{ color: STATUS.ikkeForbundet, fontWeight: 600 }}>Standby</span> = ikke kørt for nylig — ikke en fejl,
        blot ingen opgaver i perioden.{' '}
        <span style={{ color: STATUS.fejl, fontWeight: 600 }}>Fejl</span> = reel fejl-tilstand kræver opmærksomhed.
      </div>
    </ModalSkal>
  );
}

// ─── Agent-kort ───────────────────────────────────────────────────────────────

function AgentKort({ agent, timerGraense, onKlik }) {
  const farve    = agentStatusFarve(agent, timerGraense);
  const badgeStr = agentStatusBadge(agent, timerGraense);
  const [hover, setHover] = useState(false);

  return (
    <div
      onClick={() => onKlik?.(agent)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background:    hover ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
        border:        `1px solid ${hover ? KANT.aktiv : KANT.fin}`,
        borderRadius:  RADIUS.kort - 2,
        padding:       '12px 14px',
        transition:    'all 0.15s',
        cursor:        'pointer',
      }}
    >
      {/* Øverst: navn + status */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: TEKST.primær, lineHeight: 1.3 }}>
          {agent.display_navn || agent.agent_id}
        </div>
        <StatusBadge status={badgeStr} tekst={agentStatusTekst(agent, timerGraense)} lille />
      </div>

      {/* Aktuel opgave */}
      <div style={{ fontSize: 11, color: TEKST.sekundær, marginBottom: 6, minHeight: 16,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {agent.aktuel_opgave || <span style={{ color: TEKST.dæmpet, fontStyle: 'italic' }}>Ingen aktiv opgave</span>}
      </div>

      {/* Sidst aktiv */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 10, color: TEKST.dæmpet }}>Sidst aktiv</span>
        <span style={{ fontSize: 10, fontWeight: 600, color: farve }}>
          {formatRelativTid(agent.sidst_aktiv)}
        </span>
      </div>

      {/* Klik-hint */}
      {hover && (
        <div style={{ marginTop: 6, fontSize: 10, color: TEKST.dæmpet, textAlign: 'right', fontStyle: 'italic' }}>
          Klik for detaljer
        </div>
      )}

      {/* Status-prik */}
      <div style={{
        width: 6, height: 6, borderRadius: '50%',
        background: farve,
        position: 'absolute', top: 10, right: 10,
        boxShadow: agent.er_aktiv ? `0 0 6px ${farve}` : 'none',
      }} />
    </div>
  );
}

// ─── PANEL ────────────────────────────────────────────────────────────────────

export default function AgenterPanel() {
  // ── Data-fetching ──────────────────────────────────────────────────────────
  const {
    data:      overblik,
    loading:   overblikLoading,
    opdateret: opdateret,
    refresh:   overblikRefresh,
  } = useApiPolling('/os/hjerne/overblik', 90000);

  const {
    data:    kommData,
    loading: kommLoading,
  } = useApiPolling('/os/hjerne/kommunikation?limit=12', 60000);

  // ── State ──────────────────────────────────────────────────────────────────
  const [refreshing,     setRefreshing]     = useState(false);
  const [sortering,      setSortering]      = useState('status'); // 'status' | 'navn' | 'sidst_aktiv'
  const [filter,         setFilter]         = useState('alle');   // 'alle' | 'aktiv' | 'standby'
  const [valgtAgent,     setValgtAgent]     = useState(null);

  const refreshAlt = useCallback(async () => {
    setRefreshing(true);
    await overblikRefresh();
    setRefreshing(false);
  }, [overblikRefresh]);

  // ── Udled data ─────────────────────────────────────────────────────────────

  const timerGraense  = overblik?.agent_aktiv_timer_graense ?? 24;
  const alleAgenter   = overblik?.agenter || [];
  const totalAgenter  = alleAgenter.length;
  const aktiveAgenter = alleAgenter.filter(a => a.er_aktiv).length;
  const afventerDecision = overblik?.afventende_beslutninger ?? null;

  // Filtrér og sortér
  const filtreredeAgenter = (() => {
    let liste = [...alleAgenter];
    // Filter: 'aktiv' = AKTIV-badge, 'standby' = STANDBY-badge (inkl. fejl er standby-gruppen undtagen fejl)
    if (filter === 'aktiv')   liste = liste.filter(a => agentStatusBadge(a, timerGraense) === 'AKTIV');
    if (filter === 'standby') liste = liste.filter(a => agentStatusBadge(a, timerGraense) === 'STANDBY');
    if (filter === 'fejl')    liste = liste.filter(a => agentStatusBadge(a, timerGraense) === 'FEJL_AGENT');
    if (sortering === 'navn')  liste.sort((a, b) => (a.display_navn || '').localeCompare(b.display_navn || ''));
    if (sortering === 'status') liste.sort((a, b) => {
      const v = x => {
        const b = agentStatusBadge(x, timerGraense);
        return b === 'AKTIV' ? 0 : b === 'FEJL_AGENT' ? 1 : 2;
      };
      return v(a) - v(b);
    });
    if (sortering === 'sidst_aktiv') liste.sort((a, b) => {
      if (!a.sidst_aktiv) return 1;
      if (!b.sidst_aktiv) return -1;
      return new Date(b.sidst_aktiv) - new Date(a.sidst_aktiv);
    });
    return liste;
  })();

  // Kommunikation-tidslinje
  const kommTidslinje = (() => {
    const rækker = kommData?.kommunikation;
    if (!Array.isArray(rækker)) return [];
    return rækker.map((r, i) => ({
      id:    r.id || i,
      dato:  r.oprettet_kl,
      titel: `${r.fra_agent || '?'} → ${r.til_agent || '?'}`,
      tekst: r.emne || r.indhold?.slice(0, 80) || '(ingen emne)',
      farve: STATUS.ai,
      ikon:  '◉',
    }));
  })();

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: FONT.family }}>

      {/* Panel-header */}
      <SektionsHeader
        label="Agenter"
        opdateret={opdateret}
        onRefresh={refreshAlt}
        refreshing={refreshing}
        højreIndhold={
          afventerDecision > 0
            ? <AfventerBadge antal={afventerDecision} label="afventer godkendelse" />
            : null
        }
        style={{ marginBottom: 20 }}
      />

      {/* ── KPI-række ───────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 22 }}>
        <KpiKort
          label="Kanoniske agenter"
          value={totalAgenter || (overblikLoading ? null : 16)}
          farve={STATUS.konfigureret}
          ikon="◉"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Aktive lige nu"
          value={aktiveAgenter}
          farve={aktiveAgenter > 0 ? STATUS.live : STATUS.ikkeForbundet}
          ikon="▶"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Afventer René"
          value={afventerDecision}
          farve={afventerDecision > 0 ? STATUS.opmærksomhed : STATUS.live}
          ikon="⏸"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Ny viden denne uge"
          value={overblik?.laering_uge}
          farve={STATUS.ai}
          ikon="+"
          badge="LIVE"
          loading={overblikLoading}
        />
        <KpiKort
          label="Aktive projekter"
          value={overblik?.aktive_projekter}
          farve={STATUS.konfigureret}
          ikon="▦"
          badge="LIVE"
          loading={overblikLoading}
        />
      </div>

      {/* ── Agent-grid ──────────────────────────────────────────────────────── */}
      <Boks style={{ marginBottom: 22 }}>
        {/* Sub-header med filter og sortering */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <SektionsHeader label={`Agenter (${filtreredeAgenter.length})`} style={{ marginBottom: 0 }} />
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {/* Filter */}
            {[
              { key: 'alle',    label: 'Alle' },
              { key: 'aktiv',   label: 'Aktive' },
              { key: 'standby', label: 'Standby' },
              { key: 'fejl',    label: 'Fejl' },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                style={{
                  background:   filter === f.key ? 'rgba(63,212,199,0.15)' : 'none',
                  border:       `1px solid ${filter === f.key ? 'rgba(63,212,199,0.35)' : KANT.standard}`,
                  borderRadius: RADIUS.knap,
                  color:        filter === f.key ? '#3fd4c7' : TEKST.sekundær,
                  fontSize:     11, fontWeight: 600, padding: '3px 10px', cursor: 'pointer',
                  fontFamily:   FONT.family,
                }}
              >
                {f.label}
              </button>
            ))}
            <div style={{ width: 1, background: KANT.standard, margin: '0 2px' }} />
            {/* Sortering */}
            {[
              { key: 'status',      label: 'Status' },
              { key: 'navn',        label: 'Navn' },
              { key: 'sidst_aktiv', label: 'Sidst aktiv' },
            ].map(s => (
              <button
                key={s.key}
                onClick={() => setSortering(s.key)}
                style={{
                  background:   sortering === s.key ? 'rgba(139,92,246,0.12)' : 'none',
                  border:       `1px solid ${sortering === s.key ? 'rgba(139,92,246,0.30)' : KANT.standard}`,
                  borderRadius: RADIUS.knap,
                  color:        sortering === s.key ? '#a78bfa' : TEKST.sekundær,
                  fontSize:     11, padding: '3px 10px', cursor: 'pointer',
                  fontFamily:   FONT.family,
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {overblikLoading ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter agenter…</div>
        ) : alleAgenter.length === 0 ? (
          <TomTilstand
            titel="Ingen agenter fundet"
            besked="Data hentes fra os.hjerne_agent_status WHERE arkiveret=false via /api/os/hjerne/overblik"
            ikon="◉"
            badge="KONFIGURERET"
          />
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: 10,
          }}>
            {filtreredeAgenter.map(agent => (
              <div key={agent.agent_id} style={{ position: 'relative' }}>
                <AgentKort agent={agent} timerGraense={timerGraense} onKlik={setValgtAgent} />
              </div>
            ))}
          </div>
        )}

        {/* Aktiv-timer-grænse-info */}
        {!overblikLoading && (
          <div style={{ marginTop: 14, fontSize: 11, color: TEKST.dæmpet }}>
            Aktiv-grænse: {timerGraense}t (fra app_indstillinger) ·
            <StatusBadge status="LIVE" lille style={{ marginLeft: 6, verticalAlign: 'middle' }} />
          </div>
        )}
      </Boks>

      {/* ── Agent-detalje modal ──────────────────────────────────────────────── */}
      {valgtAgent && (
        <AgentDetaljeModal
          agent={valgtAgent}
          timerGraense={timerGraense}
          onLuk={() => setValgtAgent(null)}
        />
      )}

      {/* ── Agent-kommunikation tidslinje ────────────────────────────────────── */}
      <Boks>
        <SektionsHeader label="Agent-kommunikation — seneste" style={{ marginBottom: 12 }} />
        {kommLoading ? (
          <div style={{ color: TEKST.sekundær, fontSize: 13 }}>Henter…</div>
        ) : kommTidslinje.length === 0 ? (
          <TomTilstand
            titel="Ingen agent-kommunikation registreret"
            besked="Kommunikation logges i os.agent_kommunikation / os.hjerne_kommunikation. Vises her når agenter begynder at kommunikere."
            ikon="◉"
            badge="LIVE"
          />
        ) : (
          <TidslinjeListe
            items={kommTidslinje}
            tomTekst="Ingen kommunikation"
            loading={false}
            maxHøjde={320}
          />
        )}
        <div style={{ marginTop: 10, fontSize: 11, color: TEKST.sekundær }}>
          Data: os.hjerne_kommunikation / os.agent_kommunikation
          <StatusBadge status="LIVE" lille style={{ marginLeft: 8, verticalAlign: 'middle' }} />
        </div>
      </Boks>
    </div>
  );
}
